/**
 * Doomsworn — abilities
 *
 * Six d20 abilities, unchanged, plus ATTUNEMENT as a seventh.
 *
 * ATTUNEMENT is soul-weight: the casting stat for Pacts, the ceiling on rune
 * ratings, and the resistance stat in will-contests. It is rolled and modified
 * exactly like any other ability — `floor((score - 10) / 2)`.
 *
 * CRITICAL COMPATIBILITY RULE
 * ---------------------------
 * Open5e creatures have no Attunement score. An *absent* Attunement is not a
 * score of 0 (which would read as a -5 modifier and quietly penalise every
 * creature in the bestiary) — it is a modifier of **0**, i.e. no effect. Read
 * Attunement through `getAttunementModifier()` everywhere and the entire
 * existing bestiary keeps working untouched.
 */
/** Ability id for the seventh ability. */
export const ATTUNEMENT = 'atn';
/** The six d20 ability ids, kept verbatim for engine/bestiary compatibility. */
export const CORE_ABILITY_IDS = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
/** All seven Doomsworn ability ids. */
export const DOOMSWORN_ABILITY_IDS = [...CORE_ABILITY_IDS, ATTUNEMENT];
export const DOOMSWORN_ABILITIES = [
    { id: 'str', name: 'Strength', abbreviation: 'STR', description: 'Physical power, athletics, melee attacks' },
    { id: 'dex', name: 'Dexterity', abbreviation: 'DEX', description: 'Agility, reflexes, ranged attacks, AC' },
    { id: 'con', name: 'Constitution', abbreviation: 'CON', description: 'Health, stamina, hit points' },
    { id: 'int', name: 'Intelligence', abbreviation: 'INT', description: 'Knowledge, reasoning, and the Cast pillar — magic out of books' },
    { id: 'wis', name: 'Wisdom', abbreviation: 'WIS', description: 'Perception, insight, reading a room and an instrument' },
    { id: 'cha', name: 'Charisma', abbreviation: 'CHA', description: 'Force of personality, standing, negotiation with the living' },
    {
        id: ATTUNEMENT,
        name: 'Attunement',
        abbreviation: 'ATN',
        description: 'Soul-weight. How much of you there is for a Power to get hold of, and how much of a rune you can carry without becoming it. ' +
            'Casting stat for Pacts, ceiling on rune ratings, resistance in will-contests. High Attunement is not safety — it is capacity, ' +
            'which is exactly what makes you worth approaching. Creatures without an Attunement score simply have a modifier of 0.',
    },
];
/** The d20 modifier curve. Identical for all seven abilities. */
export function getModifier(score) {
    return Math.floor((score - 10) / 2);
}
/**
 * Attunement score, or `undefined` when the sheet has none (every Open5e
 * creature). Never coerce this to 0 — see `getAttunementModifier`.
 */
export function getAttunementScore(abilities) {
    const score = abilities?.[ATTUNEMENT];
    return typeof score === 'number' ? score : undefined;
}
/**
 * Attunement modifier, defaulting to 0 for anything that has no Attunement
 * score at all. This is the one function the rest of the system (and any
 * engine code touching Open5e statblocks) should use.
 */
export function getAttunementModifier(abilities) {
    const score = getAttunementScore(abilities);
    return score === undefined ? 0 : getModifier(score);
}
/** True if this sheet actually carries a soul the Powers can get hold of. */
export function hasAttunement(abilities) {
    return getAttunementScore(abilities) !== undefined;
}
/**
 * Fill in the seven abilities from a partial set. Missing core abilities
 * default to 10; Attunement is only present if it was explicitly given, so
 * that imported creatures stay attunement-less.
 */
export function normalizeAbilities(partial) {
    const out = {};
    for (const id of CORE_ABILITY_IDS) {
        out[id] = partial?.[id] ?? 10;
    }
    if (partial && typeof partial[ATTUNEMENT] === 'number') {
        out[ATTUNEMENT] = partial[ATTUNEMENT];
    }
    return out;
}
//# sourceMappingURL=abilities.js.map